// ABOUTME: Renders CRM connection controls backed by Rollout and native CRM credential actions.
// ABOUTME: Supports compact onboarding use cases and the full account settings integration picker.
"use client";

import "./rollout.css";
import React, { forwardRef, useCallback, useEffect, useState, useImperativeHandle } from "react";
import Image from "next/image";
import { Row } from "../row/Row";
import { cn } from "@/lib/utils";
import { Text } from "../text/Text";
import { v4 as uuidv4 } from "uuid";
import { useAction } from "convex/react";
import { Column } from "../column/Column";
import { Button } from "../button/button";
import { useRouter } from "next/navigation";
import { withUser } from "@/hooks/with-user";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { api } from "@/convex/_generated/api";
import { AnimatePresence, motion } from "motion/react";
import { useCrmStatus } from "@/app/_hooks/use-crm-status";
import { ArrowLeft, Info, Loader2, Link2, ExternalLink } from "lucide-react";
import { SupportOutreach } from "../support-outreach/SupportOutreach";
import { RolloutLinkProvider, CredentialForm } from "@rollout/link-react";
import { getCrmName, primaryCrmSlugs, secondaryCrmSlugs } from "@/config/crm-logos";
import { ConnectedCrmStatus } from "@/components/integration-card/ConnectedCrmStatus";
import { useRollout, RolloutCredential, ConnectorInputField } from "@/app/_hooks/use-rollout";

interface ConnectCrmButtonsProps {
  returnToRoute?: string;
  onConnected?: () => void;
  initialAppKey?: string | null;
  compact?: boolean;
  onCrmSelected?: (appKey: string | null) => void;
  hideChooseAnotherCrmButton?: boolean;
}

export const ConnectCrmButtons = forwardRef<
  { reset: () => void },
  ConnectCrmButtonsProps
>(({
  returnToRoute,
  onConnected,
  initialAppKey,
  compact = false,
  onCrmSelected,
  hideChooseAnotherCrmButton = false,
}, ref) => {
  const { fetchToken, createCredential, deleteCredential, connectors, handleCredentialAdded, handleCredentialDeleted } =
    useRollout();

  const { team } = withUser();

  const [appKey, setAppKey] = useState<string | null>(team.crm?.appKey ?? initialAppKey ?? null);
  const [isAuthInputsVisible, setIsAuthInputsVisible] = useState(false);
  const [userInputs, setUserInputs] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAllCrms, setShowAllCrms] = useState(false);

  const router = useRouter();

  useImperativeHandle(ref, () => {
    return {
      reset: () => {
        setAppKey(null);
      },
    };
  });

  // Check connection status
  const { isConnected } = useCrmStatus();

  const selectAppKey = useCallback(
    (nextAppKey: string | null) => {
      setAppKey(nextAppKey);
      onCrmSelected?.(nextAppKey);
    },
    [onCrmSelected],
  );

  const handleFubOAuth = () => {
    const clientId = process.env.NEXT_PUBLIC_FUB_CLIENT_ID;
    const redirectUri = process.env.NEXT_PUBLIC_FUB_REDIRECT_URI;

    if (!clientId || !redirectUri) {
      console.error("Missing Follow Up Boss OAuth configuration", {
        hasClientId: Boolean(clientId),
        hasRedirectUri: Boolean(redirectUri),
      });
      setError("Follow Up Boss integration is not configured correctly. Please contact support and try again later.");
      return;
    }

    const state = uuidv4();
    sessionStorage.setItem("fubOauthState", state);
    const ret = returnToRoute ?? window.location.pathname + window.location.search;
    localStorage.setItem("fubReturnToRoute", JSON.stringify(ret));
    const oauthUrl =
      `https://app.followupboss.com/oauth/authorize?response_type=auth_code` +
      `&client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&state=${state}`;
    window.location.href = oauthUrl;
  };

  useEffect(() => {
    if (team.crm?.appKey) {
      if (team.crm.status === "connected") {
        setAppKey(team.crm.appKey);
      } else {
        setAppKey(null);
      }
      return;
    }

    if (initialAppKey && initialAppKey !== "follow-up-boss") {
      setAppKey(initialAppKey);
    }
  }, [initialAppKey, team.crm?.appKey, team.crm?.status]);

  useEffect(() => {
    if (!appKey) {
      setIsAuthInputsVisible(false);
      return;
    }

    const checkVisibility = () => {
      if (typeof document === "undefined") {
        setIsAuthInputsVisible(false);
        return;
      }

      const element = document.querySelector(".universal-AuthUserInputs-root");
      const groupElement = document.querySelector(".universal-Group-root");
      setIsAuthInputsVisible(!!element || !!groupElement);
    };

    checkVisibility();

    const observer = new MutationObserver(() => {
      checkVisibility();
    });

    if (typeof document !== "undefined" && document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return () => observer.disconnect();
  }, [appKey]);

  // Reset form inputs when connector changes
  useEffect(() => {
    setUserInputs({});
    setError(null);
  }, [appKey]);

  const inputFields: ConnectorInputField[] = (() => {
    if (!connectors || !appKey || !connectors[appKey]) return [];
    const connector = connectors[appKey];
    // Prefer plan-specific auth schema, fall back to default
    const auth = connector.authByPlan?.standard ?? connector.auth;
    return auth?.userInputsSchema ?? [];
  })();

  const allInputsFilled = inputFields.length > 0 && inputFields.every((field) => userInputs[field.key]?.trim());

  const validateBoldTrailApiKey = useAction(api.crmSync.actions.validateBoldTrailApiKey);

  const handleConnect = async (appKey: string) => {
    setError(null);
    setIsSubmitting(true);

    // Pre-connection probe for BoldTrail. Only BLOCKS on `invalid` (auth
    // failure, truly bad token). `list_only` proceeds — the list endpoint
    // includes phones + basic fields, downstream skip-trace recovers
    // addresses, and the static `boldTrailNote` above the form already
    // warns users about Team Account limits. Probe throws are non-fatal too
    // — let Rollout be the source of truth on bad keys.
    // Length >10 matches the activationSync candidate extraction so both
    // pick the same userInputs field when the connector uses a non-`apiKey`
    // field name.
    if (appKey === "boldtrail") {
      const candidateKey = Object.values(userInputs).find((v) => typeof v === "string" && v.trim().length > 10);
      if (candidateKey) {
        try {
          const check = await validateBoldTrailApiKey({ apiKey: candidateKey });
          if (check.kind === "invalid") {
            setError(check.userMessage);
            setIsSubmitting(false);
            return { success: false, error: check.userMessage };
          }
        } catch (err) {
          console.warn(`[BoldTrail] Pre-connect probe failed: ${(err as Error).message}`);
        }
      }
    }

    const result = await createCredential(appKey, userInputs);
    if (result.success) {
      if (onConnected) {
        onConnected();
      } else if (returnToRoute) {
        router.push(returnToRoute);
      }
    }
    // Complete the connection flow
    setIsSubmitting(false);
    return result;
  };

  const handleRolloutCredentialAdded = useCallback(
    async (credential: RolloutCredential) => {
      const result = await handleCredentialAdded(credential);
      if (result.success) {
        if (onConnected) {
          onConnected();
        } else if (returnToRoute) {
          router.push(returnToRoute);
        }
      }
      return result;
    },
    [handleCredentialAdded, onConnected, returnToRoute, router],
  );

  const CrmButtonWrapper = ({
    children,
    onClick,
    className,
  }: {
    children: React.ReactNode;
    onClick: () => void;
    className?: string;
  }) => {
    return (
      <div
        className={cn(
          "flex items-center justify-center bg-white shadow-xs rounded-md hover:bg-muted/50 border border-border cursor-pointer",
          compact ? "h-fit min-h-0 w-full p-3" : "h-48 w-48",
          className,
        )}
        onClick={onClick}
      >
        {children}
      </div>
    );
  };

  const errorMessage = (
    <AnimatePresence>
      {error && (
        <motion.div
          key="error-message"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col gap-2 p-4 bg-rose-50 rounded-md border border-rose-200 overflow-clip"
        >
          <Row align tightGap className="text-rose-600">
            <Info className="h-4 w-4 shrink-0" />
            <Column tightGap wide>
              <Text sm className="text-rose-600">
                {error}
              </Text>
            </Column>
          </Row>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const boldTrailNote =
    appKey === "boldtrail" ? (
      <Row className="gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
        <Column tightGap>
          <Row tightGap>
            <Info className="h-4 w-4 shrink-0 mt-[2.5px]" />
            <Column tightGap>
              <Text sm fat className="text-amber-900">
                BoldTrail Team Account API keys are not supported
              </Text>
              <Text sm>
                To continue, create an individual user account key: {" "}

                <a
                  href="https://help.insiderealestate.com/en/articles/4263959-boldtrail-api-tokens"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium inline-block"
                >
                  <Row tightGap className="flex">
                    BoldTrail API token instructions
                    <ExternalLink className="h-4 w-4 shrink-0 mt-[2.5px]" />
                  </Row>
                </a>

              </Text>
            </Column>
          </Row>
        </Column>
      </Row >
    ) : null;
  /**
   * Only show the loader icon if universal-AuthUserInputs-root is not visible or non-existent
   */
  const selectedConnectorAvailable = !appKey || !connectors || Boolean(connectors[appKey]);
  const connectorView = appKey && selectedConnectorAvailable ? (
    <Column wide className="w-full">
      <Column className="bg-white rounded-lg border border-border shadow-xs overflow-clip" wide>
        <Column className="bg-white p-4 border-b border-border" wide>
          <Row tightGap align className="pt-2">
            <Image src={`/images/crms/${appKey}.svg`} width={24} height={24} alt={appKey} className="h-6 w-6" />
            <Text base fat className="whitespace-nowrap text-center">
              Connect your {getCrmName(appKey)} account
            </Text>
          </Row>
          <Text sm>
            Paste your {getCrmName(appKey)} API key. We require it to sync your contacts and prepare enrichment before your onboarding call.
          </Text>
          {boldTrailNote}
          {errorMessage}
        </Column>
        <Column className="p-4 pt-0">
          {/* Secondary CRMs use the CredentialForm component, as some have extra fields like Gmail OTP inputs, etc. */}
          {secondaryCrmSlugs.includes(appKey) ? (
            <CredentialForm
              appKey={appKey}
              onCredentialAdded={handleRolloutCredentialAdded}
              onCredentialDeleted={handleCredentialDeleted}
              showUserTermsLink={false}
            />
          ) : // Primary CRMs use the traditional input fields, as they don't have any extra fields.
            inputFields.length > 0 ? (
              <Column>
                {inputFields.map((field) => (
                  <Column key={field.key}>
                    <Column noGap>
                      <Label htmlFor={field.key} className="text-sm text-foreground font-bold">
                        {field.label}
                      </Label>
                      {field.infoHTML && (
                        <div className="text-sm text-foreground" dangerouslySetInnerHTML={{ __html: field.infoHTML }} />
                      )}
                    </Column>

                    <Row align className="flex-none relative">
                      <Input
                        id={field.key}
                        className={cn("w-full mt-1", inputFields.length === 1 && "h-14 pr-32!")}
                        placeholder={field.placeholder}
                        value={userInputs[field.key] ?? ""}
                        onChange={(e) => setUserInputs((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      />
                      {inputFields.length === 1 && (
                        <Button
                          variant="green"
                          size="sm"
                          onClick={() => handleConnect(appKey)}
                          disabled={!allInputsFilled || isSubmitting}
                          className="absolute right-2 top-1/2 h-10 -translate-y-1/2 rounded-md px-5 active:-translate-y-[45%]"
                          asChild
                        >
                          <motion.div
                            initial={{ opacity: 0, width: 0 }}
                            animate={{ opacity: 1, width: "auto" }}
                            exit={{ opacity: 0, width: 0 }}
                            transition={{ delay: 0.5 }}
                          >
                            <motion.div className="flex flex-row items-center">
                              <AnimatePresence>
                                {isSubmitting && (
                                  <motion.div
                                    key="loading"
                                    initial={{ opacity: 0, width: 0 }}
                                    animate={{ opacity: 1, width: "20px" }}
                                    exit={{ opacity: 0, width: 0 }}
                                  >
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  </motion.div>
                                )}
                                <motion.div key="text">
                                  <Text sm>Connect</Text>
                                </motion.div>
                              </AnimatePresence>
                            </motion.div>
                          </motion.div>
                        </Button>
                      )}
                    </Row>
                  </Column>
                ))}
                {inputFields.length > 1 && (
                  <Row justifyEnd>
                    <Button
                      variant="green"
                      className="w-fit"
                      size="sm"
                      onClick={() => handleConnect(appKey)}
                      disabled={!allInputsFilled || isSubmitting}
                    >
                      <motion.div
                        initial={{ opacity: 0, height: 0, width: 0 }}
                        animate={{ opacity: 1, height: "auto", width: "auto" }}
                        exit={{ opacity: 0, height: 0, width: 0 }}
                        className="flex flex-row items-center"
                      >
                        <AnimatePresence>
                          {isSubmitting && (
                            <motion.div
                              key="loading"
                              initial={{ opacity: 0, width: 0 }}
                              animate={{ opacity: 1, width: "20px" }}
                              exit={{ opacity: 0, width: 0 }}
                            >
                              <Loader2 className="h-4 w-4 animate-spin" />
                            </motion.div>
                          )}
                          <motion.div
                            key="text"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                          >
                            <Row shrink align tightGap>
                              <Text sm>Connect</Text>
                              <Link2 className="h-4 w-4" />
                            </Row>
                          </motion.div>
                        </AnimatePresence>
                      </motion.div>
                    </Button>
                  </Row>
                )}
              </Column>
            ) : (
              !connectors && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            )}
        </Column>
        {!hideChooseAnotherCrmButton && (
          <Row align className="min-h-0 p-4 border-t border-border">
            <Button
              size="xs"
              variant="ghost"
              justify="start"
              onClick={() => selectAppKey(null)}
              className="w-fit px-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Choose another CRM
            </Button>
          </Row>
        )}
      </Column>
      {error && <SupportOutreach />}

    </Column>
  ) : null;

  if (isConnected) {
    return (
      <ConnectedCrmStatus
        onReconnect={async () => {
          await deleteCredential();
          setAppKey(null);
        }}
      />
    );
  }

  // FUB connects via our own OAuth flow; every other CRM comes from Rollout.
  const primaryRolloutSlugs = primaryCrmSlugs.filter((slug) => slug !== "follow-up-boss");
  const renderCrmCard = (connector: string) => {
    const isFub = connector === "follow-up-boss";
    return (
      <CrmButtonWrapper
        key={connector}
        onClick={() => (isFub ? handleFubOAuth() : selectAppKey(connector))}
        className="w-full h-fit p-4"
      >
        <Row align>
          <Image src={`/images/crms/${connector}.svg`} width={16} height={16} alt={connector} />
          <Text fat>{getCrmName(connector)}</Text>
        </Row>
      </CrmButtonWrapper>
    );
  };

  return (
    <div className="rollout-embed w-full">
      <RolloutLinkProvider token={fetchToken}>
        {connectorView ?? (
          <Column wide>
            {renderCrmCard("follow-up-boss")}
            {connectors && primaryRolloutSlugs.filter((slug) => connectors[slug]).map(renderCrmCard)}
            {showAllCrms && connectors && secondaryCrmSlugs.filter((slug) => connectors[slug]).map(renderCrmCard)}
            {!showAllCrms && (
              <div className="flex justify-center">
                <Button variant="link" size="sm" onClick={() => setShowAllCrms(true)}>
                  View all CRMs
                </Button>
              </div>
            )}
          </Column>
        )}
      </RolloutLinkProvider>
    </div>
  );
});

export default ConnectCrmButtons;
